'use client'

import * as React from 'react'
import NumberFlow from '@number-flow/react'

/* Animated stat numbers. Call sites pass the exact display string from their
   copy source ("99.9%", "2,500+", "50K+/mo", "24/7") and the rendered result
   is character-for-character identical to it — only the digits move. Strings
   with no leading number ("Custom", "1 Day" parses fine, "N/A" does not) fall
   back to plain text, so any value is safe to pass. */

type Parsed = {
  prefix: string
  target: number
  suffix: string
  fractionDigits: number
  useGrouping: boolean
}

const NUMBER_RE = /\d[\d,]*(?:\.\d+)?/

function parseMetric(raw: string): Parsed | null {
  const match = NUMBER_RE.exec(raw)
  if (!match) return null
  const token = match[0]
  const target = Number.parseFloat(token.replace(/,/g, ''))
  if (!Number.isFinite(target)) return null
  const decimals = token.split('.')[1]
  return {
    prefix: raw.slice(0, match.index),
    target,
    suffix: raw.slice(match.index + token.length),
    fractionDigits: decimals ? decimals.length : 0,
    useGrouping: token.includes(','),
  }
}

type AnimatedNumberProps = {
  value: string | number
  className?: string
  /** Delay (ms) between entering the viewport and the count-up starting. */
  delay?: number
}

/* The server renders the real value (copy stays intact for no-JS readers and
   crawlers); a pre-paint effect resets it to 0 without animating, and the
   count-up runs once the element scrolls into view. Under reduced motion the
   reset never happens, so the true value simply sits there. */
export function AnimatedNumber({
  value,
  className,
  delay = 0,
}: AnimatedNumberProps) {
  const raw = typeof value === 'number' ? String(value) : value
  const parsed = React.useMemo(() => parseMetric(raw), [raw])
  const ref = React.useRef<HTMLSpanElement>(null)
  const [phase, setPhase] = React.useState<'static' | 'reset' | 'run'>('static')

  React.useLayoutEffect(() => {
    if (!parsed) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    setPhase('reset')
  }, [parsed])

  React.useEffect(() => {
    if (phase !== 'reset') return
    const el = ref.current
    if (!el) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        observer.disconnect()
        timer = setTimeout(() => setPhase('run'), delay)
      },
      { threshold: 0.4 }
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [phase, delay])

  if (!parsed) return <span className={className}>{raw}</span>

  return (
    <span ref={ref} className={className}>
      <NumberFlow
        value={phase === 'reset' ? 0 : parsed.target}
        prefix={parsed.prefix}
        suffix={parsed.suffix}
        animated={phase === 'run'}
        locales="en-US"
        format={{
          useGrouping: parsed.useGrouping,
          minimumFractionDigits: parsed.fractionDigits,
          maximumFractionDigits: parsed.fractionDigits,
        }}
        /* NumberFlow pads the element by --number-flow-mask-height (0.25em)
           top and bottom so spinning digits can fade at the edges. In the
           site's leading-none stat bands that inflates the line box (~46px
           for a 30px font), knocking the number off its siblings' baseline
           and pushing the label down. Equal negative margins cancel the
           layout cost while keeping the fade mask. */
        style={{
          margin:
            'calc(-2 * round(nearest, calc(var(--number-flow-mask-height, 0.25em) / 2), 1px)) 0',
        }}
      />
    </span>
  )
}
